import React, { useState } from 'react';
import './PaymentModal.css';
import { AssinaturaRequestDto, subscriptionService, TipoPlanoEnum } from '../../pages/private/SubscriptionPage/service/subscription.service';

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  planName: string;
  planPrice: string;
  planPeriod: string;
  planType?: TipoPlanoEnum;
  isUpdate?: boolean;
  onSuccess?: () => Promise<void> | void;
}

interface CreditCardFormProps {
  onSubmit: (data: AssinaturaRequestDto) => void;
  isLoading: boolean;
  isUpdate?: boolean;
  planType?: TipoPlanoEnum;
  requireTermsAcceptance?: boolean;
  hasAcceptedTerms?: boolean;
  onTermsAcceptanceChange?: (checked: boolean) => void;
  onOpenTerms?: () => void;
}

const CreditCardForm: React.FC<CreditCardFormProps> = ({
  onSubmit,
  isLoading,
  isUpdate,
  planType,
  requireTermsAcceptance = false,
  hasAcceptedTerms = false,
  onTermsAcceptanceChange,
  onOpenTerms,
}) => {
  const [nomeNoCartao, setNomeNoCartao] = useState('');
  const [numeroCartao, setNumeroCartao] = useState('');
  const [mesExpiracao, setMesExpiracao] = useState('');
  const [anoExpiracao, setAnoExpiracao] = useState('');
  const [ccv, setCcv] = useState('');
  const [cep, setCep] = useState('');
  const [numeroEndereco, setNumeroEndereco] = useState('');
  const [cpf, setCpf] = useState('');

  // Masking functions
  const formatCreditCardNumber = (value: string) => {
    // Remove all non-digit characters
    const digitsOnly = value.replace(/\D/g, '');
    // Insert a space every 4 digits
    return digitsOnly.replace(/(\d{4})(?=\d)/g, '$1 ');
  };

  const formatCpf = (value: string) => {
    // Remove all non-digit characters
    const digitsOnly = value.replace(/\D/g, '');
    // Apply CPF mask: 000.000.000-00
    return digitsOnly
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  };

  const formatCep = (value: string) => {
    // Remove all non-digit characters
    const digitsOnly = value.replace(/\D/g, '');
    // Apply CEP mask: 00000-000
    return digitsOnly.replace(/(\d{5})(\d)/, '$1-$2');
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    // Pass unmasked values to the service
    const data: AssinaturaRequestDto = {
      nomeNoCartao,
      numeroCartao: numeroCartao.replace(/\D/g, ''), // Remove mask for API
      mesExpiracao,
      anoExpiracao,
      ccv,
      cep: cep.replace(/\D/g, ''), // Remove mask for API
      numeroEndereco,
      cpf: cpf.replace(/\D/g, ''), // Remove mask for API
    };

    if (!isUpdate && planType) {
      data.plano = planType;
    }

    onSubmit(data);
  };

  const isSubmitDisabled = isLoading || (requireTermsAcceptance && !hasAcceptedTerms);

  return (
    <form className="credit-card-form" onSubmit={handleSubmit}>
      <div className="input-group">
        <label className="input-label">Número do Cartão</label>
        <input
          type="text"
          className="input-field"
          placeholder="0000 0000 0000 0000"
          value={numeroCartao}
          onChange={(e) => setNumeroCartao(formatCreditCardNumber(e.target.value))}
          required
          maxLength={19} // 16 digits + 3 spaces
        />
      </div>
      <div className="input-group">
        <label className="input-label">Nome do Titular</label>
        <input
          type="text"
          className="input-field"
          placeholder="Nome como no cartão"
          value={nomeNoCartao}
          onChange={(e) => setNomeNoCartao(e.target.value)}
          required
        />
      </div>
      <div style={{ display: 'flex', gap: '1rem' }}>
        <div className="input-group" style={{ flex: 1 }}>
          <label className="input-label">Mês de Validade</label>
          <input
            type="text"
            className="input-field"
            placeholder="MM"
            value={mesExpiracao}
            onChange={(e) => setMesExpiracao(e.target.value.replace(/\D/g, ''))} // Only digits
            required
            maxLength={2}
          />
        </div>
        <div className="input-group" style={{ flex: 1 }}>
          <label className="input-label">Ano de Validade</label>
          <input
            type="text"
            className="input-field"
            placeholder="AA"
            value={anoExpiracao}
            onChange={(e) => setAnoExpiracao(e.target.value.replace(/\D/g, ''))} // Only digits
            required
            maxLength={2}
          />
        </div>
        <div className="input-group" style={{ flex: 1 }}>
          <label className="input-label">CVV</label>
          <input
            type="text"
            className="input-field"
            placeholder="123"
            value={ccv}
            onChange={(e) => setCcv(e.target.value.replace(/\D/g, ''))} // Only digits
            required
            maxLength={3}
          />
        </div>
      </div>
      <div className="input-group">
        <label className="input-label">CEP</label>
        <input
          type="text"
          className="input-field"
          placeholder="00000-000"
          value={cep}
          onChange={(e) => setCep(formatCep(e.target.value))}
          required
          maxLength={9} // 8 digits + 1 hyphen
        />
      </div>
      <div className="input-group">
        <label className="input-label">Número do Endereço</label>
        <input
          type="text"
          className="input-field"
          placeholder="Ex: 123"
          value={numeroEndereco}
          onChange={(e) => setNumeroEndereco(e.target.value)}
          required
        />
      </div>
      <div className="input-group">
        <label className="input-label">CPF do Titular</label>
        <input
          type="text"
          className="input-field"
          placeholder="000.000.000-00"
          value={cpf}
          onChange={(e) => setCpf(formatCpf(e.target.value))}
          required
          maxLength={14} // 11 digits + 2 dots + 1 hyphen
        />
      </div>

      {requireTermsAcceptance && (
        <div className="terms-acceptance">
          <label className="terms-acceptance-label">
            <input
              type="checkbox"
              checked={hasAcceptedTerms}
              onChange={(e) => onTermsAcceptanceChange?.(e.target.checked)}
              required
            />
            <span>
              Li e concordo com os{' '}
              <button type="button" className="terms-link-button" onClick={onOpenTerms}>
                Termos de Assinatura
              </button>{' '}
              e entendo que serei cobrado automaticamente após os 7 dias de teste grátis caso não cancele.
            </span>
          </label>
        </div>
      )}

      <button
        type="submit"
        className={`btn btn-primary ${isSubmitDisabled ? 'btn-disabled' : ''}`}
        style={{ width: '100%', marginTop: '1rem' }}
        disabled={isSubmitDisabled}
        aria-disabled={isSubmitDisabled}
      >
        {isLoading ? 'Processando...' : isUpdate ? 'Atualizar Cartão' : 'Pagar com Cartão'}
      </button>
    </form>
  );
};

const PaymentModal: React.FC<PaymentModalProps> = ({
  isOpen,
  onClose,
  planName,
  planPrice,
  planPeriod,
  planType,
  isUpdate = false,
  onSuccess,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);

  const termsText = `TERMOS E CONDIÇÕES DE ASSINATURA – SINALINVEST
Última atualização: Fevereiro de 2026

Ao assinar a plataforma SinalInvest, você ("USUÁRIO") concorda com os termos abaixo descritos. A utilização dos serviços implica na aceitação integral destas condições.

1. DO PERÍODO DE TESTE GRATUITO (TRIAL DE 7 DIAS)
1.1. Mecanismo: A SinalInvest oferece um período de teste gratuito de 7 (sete) dias corridos para novos assinantes. Para ativar o teste, o USUÁRIO deve cadastrar um meio de pagamento válido (Cartão de Crédito).

1.2. Cobrança: Nenhuma cobrança será realizada no momento do cadastro. O valor integral do plano escolhido será debitado automaticamente no cartão cadastrado apenas no 8º (oitavo) dia, caso o USUÁRIO não efetue o cancelamento antes desse prazo.

1.3. Cancelamento do Teste: O USUÁRIO pode cancelar a assinatura a qualquer momento dentro dos 7 dias de teste através do painel de controle ("Minha Conta"). Neste caso, nenhuma cobrança será efetuada e o acesso será interrompido ao final do 7º dia.

2. DOS PLANOS E PAGAMENTOS
2.1. Renovação Automática: Todos os planos (Mensal ou Anual) funcionam sob o modelo de assinatura recorrente. A renovação ocorre automaticamente ao final de cada ciclo, utilizando o mesmo meio de pagamento, salvo se houver cancelamento prévio.

2.2. Plano Mensal: Garante acesso à plataforma por 30 dias. A cobrança é realizada mensalmente.
2.3. Plano Anual: Garante acesso à plataforma por 12 meses. O valor total pode ser pago à vista ou parcelado, conforme condições comerciais vigentes. O desconto oferecido no plano anual é condicionado à permanência pelo período de 12 meses.

3. POLÍTICA DE CANCELAMENTO E REEMBOLSO
3.1. Após o Período de Teste (7 dias): Passado o período de teste gratuito e efetuada a primeira cobrança, o serviço é considerado contratado e entregue.

3.2. Cancelamento de Plano Mensal: O USUÁRIO pode cancelar a renovação a qualquer momento. O acesso permanecerá ativo até o fim do ciclo mensal já pago. Não haverá reembolso proporcional (pró-rata) dos dias não utilizados.

3.3. Cancelamento de Plano Anual: O cancelamento impede a renovação automática para o próximo ano. O USUÁRIO continuará com acesso até o término dos 12 meses contratados. Não haverá reembolso total ou parcial do valor pago, visto que o desconto concedido pressupõe a fidelidade contratual de 1 ano.

4. ISENÇÃO DE RESPONSABILIDADE FINANCEIRA (DISCLAIMER)
4.1. Natureza do Serviço: A SinalInvest é uma ferramenta tecnológica de análise de dados baseada em Inteligência Artificial. Não somos uma casa de análise (research), consultoria de valores mobiliários ou gestora de recursos.

4.2. Ausência de Recomendação: As classificações de impacto ("Positivo", "Negativo", "Neutro") geradas pela nossa IA referem-se estritamente à interpretação semântica da notícia e dados correlatos. Nenhuma informação na plataforma constitui recomendação de compra, venda ou manutenção de ativos financeiros.

4.3. Riscos de Mercado: O mercado financeiro é volátil e envolve riscos. O USUÁRIO reconhece que é o único responsável por suas decisões de investimento. A SinalInvest não se responsabiliza por quaisquer perdas financeiras, danos diretos ou indiretos decorrentes do uso das informações fornecidas pela plataforma.

4.4. Precisão da IA: Embora utilizemos tecnologia de ponta, modelos de Inteligência Artificial podem cometer erros de interpretação ("alucinações"). O USUÁRIO deve sempre verificar as fontes originais das notícias (https://www.google.com/search?q=links fornecidos) antes de tomar decisões críticas.

5. PROPRIEDADE INTELECTUAL E USO DE DADOS
5.1. É estritamente proibido copiar, redistribuir, vender ou realizar engenharia reversa ("scraping") dos dados, análises ou estrutura da plataforma SinalInvest sem autorização expressa.

5.2. O compartilhamento de login e senha é proibido. O sistema possui monitoramento de sessões simultâneas e poderá bloquear contas com atividade suspeita de compartilhamento.

6. DISPOSIÇÕES GERAIS
6.1. A SinalInvest reserva-se o direito de alterar os preços dos planos mediante aviso prévio de 30 dias via e-mail. O USUÁRIO terá o direito de cancelar a assinatura antes da vigência do novo valor caso não concorde com o reajuste.`;

  const handleProcessPayment = async (data: AssinaturaRequestDto) => {
    if (!isUpdate && !hasAcceptedTerms) {
      setError('Você precisa aceitar os Termos de Assinatura para continuar.');
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      if (isUpdate) {
        await subscriptionService.atualizarCartao(data);
      } else {
        await subscriptionService.criarAssinatura(data);
      }
      setPaymentSuccess(true);
      if (!isUpdate && onSuccess) {
        await onSuccess();
      }
    } catch (err) {
      setError(`Falha ao ${isUpdate ? 'atualizar o cartão' : 'processar o pagamento'}. Tente novamente.`);
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={`modal-overlay ${isOpen ? 'show' : ''}`} onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2>{isUpdate ? 'Atualizar Cartão' : 'Finalizar Assinatura'}</h2>
            <p style={{ margin: 0, color: 'var(--text-secondary)'}}>
              {isUpdate ? `Plano ${planName}` : `Plano ${planName} - R$ ${planPrice} / ${planPeriod}`}
            </p>
          </div>
          <button className="close-modal-btn" onClick={onClose}>
            &times;
          </button>
        </div>

        <div className="payment-method-content">
          {paymentSuccess ? (
            <div className="payment-success">
              <h3>{isUpdate ? 'Cartão atualizado com sucesso!' : 'Assinatura realizada com sucesso!'}</h3>
              <p>{isUpdate ? 'Suas próximas faturas serão cobradas no novo cartão.' : `Obrigado por assinar o plano ${planName}.`}</p>
              <button className="btn btn-primary" onClick={onClose}>Fechar</button>
            </div>
          ) : (
            <>
              {error && <div className="error-message">{error}</div>}
              <CreditCardForm
                onSubmit={handleProcessPayment}
                isLoading={isLoading}
                isUpdate={isUpdate}
                planType={planType}
                requireTermsAcceptance={!isUpdate}
                hasAcceptedTerms={hasAcceptedTerms}
                onTermsAcceptanceChange={setHasAcceptedTerms}
                onOpenTerms={() => setIsTermsModalOpen(true)}
              />
            </>
          )}
        </div>
      </div>

      {isTermsModalOpen && (
        <div className="terms-overlay" onClick={() => setIsTermsModalOpen(false)}>
          <div className="terms-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="terms-modal-header">
              <h3>Termos de Assinatura</h3>
              <button
                type="button"
                className="close-modal-btn"
                onClick={() => setIsTermsModalOpen(false)}
              >
                &times;
              </button>
            </div>
            <pre className="terms-text">{termsText}</pre>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentModal;
